import { useMemo } from "react";

import useAuth from "contexts/Auth/useAuth";
import { useOrganizations } from "contexts/Organizations";
import { useCurrentProject } from "contexts/Projects";
import {
	BMS_ORG_ID,
	BMS_STAGE_ORG_ID,
	BMS_TEST_ORG_ID,
} from "views/SharedScene/StreamingStatesConstants";

/**
 * Whether an organization id is the BMS one, matched across environments
 * (prod / stage / internal test) so anything keyed on "is this BMS" behaves the
 * same wherever it is being worked on.
 */
export const isBmsOrganizationId = (id?: string): boolean =>
	id === BMS_ORG_ID || id === BMS_STAGE_ORG_ID || id === BMS_TEST_ORG_ID;

/**
 * Whether the user is working in a consumer-side context right now, and so must
 * not be shown business features (BCF Issue Management, the whole-catalog
 * Library, …).
 *
 * NOT the same question as `isB2cUser(user)` in contexts/Auth, despite the
 * shared wording — and both are right for what they gate.
 *
 * That one reads `user.scope`, a deliberate ENTITLEMENT marker on the account:
 * it decides who gets the reduced library (combinations and curated materials,
 * no objects or light sources) and the scene cap, and the server enforces the
 * same boundary. BMS ProfiPartners carry `b2c` on purpose for exactly that
 * reason, business users though they are — so anything about what the catalog
 * may offer belongs on `isB2cUser`, not here.
 *
 * This one is about the CONTEXT: which organization is open, and what this user
 * is to the project in it. It gates features that make no sense around a
 * consumer's single project (BCF Issue Management), not catalog entitlements.
 *
 * Three ways to be in one, because a BMS endcustomer can be recorded in more
 * than one of them:
 *
 *  1. their own personal space — a single-project consumer workspace;
 *  2. a junior in the BMS org, either at org level (a ProfiPartner) or on this
 *     project (an endcustomer in the project created for them —
 *     createBmsEndcustomerProject in hegiasGo adds them as junior);
 *  3. the person the project's own `endcustomerEmail` names, whichever
 *     organization that project lives in. The sturdiest of the three: it comes
 *     from the project record rather than from role bookkeeping or an org id
 *     constant, so it is checked unscoped.
 *
 * Lives in one place because it had already drifted: Project.tsx and Scene.tsx
 * each carried their own copy, and only one of them knew about the
 * project-level cases.
 */
const useIsB2cContext = (): boolean => {
	const { me } = useAuth();
	const { current, member } = useOrganizations();
	const currentProject = useCurrentProject();

	// This user's role ON THE CURRENT PROJECT, which is not their org role.
	const projectMemberRole = useMemo(() => {
		const userId = member?.user?._id;
		const members = (currentProject as any)?.members as any[] | undefined;
		if (!userId || !members) {
			return undefined;
		}
		return members.find((m) => String(m.id) === String(userId))?.role;
	}, [currentProject, member?.user?._id]);

	const isProjectEndcustomer = useMemo(() => {
		const endcustomerEmail = (currentProject as any)?.endcustomerEmail as
			| string
			| undefined;
		if (!endcustomerEmail) {
			return false;
		}
		const normalized = endcustomerEmail.toLowerCase();
		return Boolean(
			me?.profile?.email?.some(
				(email) => email.toLowerCase() === normalized,
			),
		);
	}, [currentProject, me?.profile?.email]);

	return Boolean(
		current?.isPersonal ||
			(isBmsOrganizationId(current?._id) &&
				(member?.role === "junior" ||
					projectMemberRole === "junior")) ||
			isProjectEndcustomer,
	);
};

export default useIsB2cContext;
